import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { routes } from '../../constants';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import StyledPage from '../../components/StyledPage';
import { axiosInstance } from '../../api/axiosInstance';

export default function ActivatePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // <600px

  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true); // Loading state

  useEffect(() => {
    async function activateAccount() {
      setIsLoading(true); // Start loading
      try {
        const res = await axiosInstance.get(`/accounts/activate/${token}`);
        console.log('res:', res);
        setSuccess(true);
      } catch (e) {
        const error = e.response?.data?.error || 'An unexpected error occurred';
        setErrorMessage(error);
        // Handle specific cases (e.g., expired/used links)
        if (e.response?.status === 400) {
          setErrorMessage(
            <FormattedMessage id="register.activation_expired" />
          ); // Add this translation
        }
      } finally {
        setIsLoading(false); // Stop loading
      }
    }

    activateAccount();
  }, [token]);

  return (
    <StyledPage
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: isMobile ? 'center' : 'space-between',
        alignItems: 'center',
        background: isMobile
          ? '#f5f5f5'
          : `url("https://images.unsplash.com/photo-1507521628349-dee6b8290f6f")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Content Section */}
      <Box
        sx={{
          flex: isMobile ? 'auto' : 1,
          p: { xs: 2, md: 5 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: isMobile ? 'transparent' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: isMobile ? 'none' : 'blur(5px)',
          borderRadius: 1,
          maxWidth: { xs: '100%', md: '500px' },
          mx: 'auto',
          my: isMobile ? 0 : { xs: 1, md: 0 },
          boxShadow: isMobile ? '0px 2px 4px rgba(0, 0, 0, 0.1)' : 0,
          transform: isMobile ? 'translateY(-10%)' : 'none',
        }}
      >
        {isMobile && (
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              color: '#00438E',
              textAlign: 'center',
              fontWeight: 'bold',
            }}
          >
            PromptaAI
          </Typography>
        )}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <CircularProgress sx={{ color: '#00438E' }} />
          </Box>
        ) : success ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: '#00438E',
                fontWeight: 'bold',
              }}
            >
              <FormattedMessage id="register.success_header" />
            </Typography>
            <Typography
              sx={{
                mb: 3,
                color: '#00438E',
                fontSize: 16,
              }}
            >
              <FormattedMessage id="register.activation_success" />
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(routes.login)}
              sx={{
                width: isMobile ? '100%' : 'auto',
                backgroundColor: '#00438E',
                '&:hover': { backgroundColor: '#003A75' },
                py: 1.5,
                px: 3,
              }}
            >
              <FormattedMessage id="login.return_to_login" />
            </Button>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: '#00438E',
                fontWeight: 'bold',
              }}
            >
              <FormattedMessage id="error.unknown_error" />
            </Typography>
            <Typography
              sx={{
                mb: 3,
                color: 'crimson',
                fontSize: 16,
              }}
            >
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(routes.login)}
              sx={{
                width: isMobile ? '100%' : 'auto',
                backgroundColor: '#00438E',
                '&:hover': { backgroundColor: '#003A75' },
                py: 1.5,
                px: 3,
              }}
            >
              <FormattedMessage id="login.return_to_login" />
            </Button>
          </Box>
        )}
      </Box>
    </StyledPage>
  );
}
